import { PLAN_FEATURES } from './plans.js';

/**
 * The free Starter plan's value on every plan-gated feature flag (ADR-007):
 * the `starter_enabled` / `starter_limit` columns of `feature_flags`.
 *
 * One list, read by the seed (prisma/seed.ts) and checked against the
 * migration that first wrote these values
 * (20261101000400_starter_flag_values) by a test, so a re-seed writes exactly
 * what the migration wrote and can never undo it.
 *
 * The owner's free plan named seven limits; they come from
 * PLAN_FEATURES.STARTER. Every feature that table never named is off or zero
 * on Starter, except three that are about safety or the customer's rights:
 * refunds of a charge (billing.refund_request), checking that a post really
 * went out (publish.verify), and the metering switch of plan regeneration,
 * which only says how a regeneration would be counted.
 */
export interface StarterFlagValue {
  enabled: boolean;
  /** null is unlimited (or no limit applies). */
  limit: number | null;
}

const S = PLAN_FEATURES.STARTER;

export const STARTER_PLAN_FLAGS: Readonly<Record<string, StarterFlagValue>> = {
  'platforms.connect': { enabled: true, limit: S.maxPlatforms },
  'posts.monthly_limit': { enabled: true, limit: S.postsPerMonth },
  'posts.scheduling': { enabled: S.scheduling, limit: null },
  // CSV import of posts (20261103010200_csv_import_flag): a paid feature.
  'posts.csv_import': { enabled: false, limit: null },
  'team.seats': { enabled: true, limit: S.teamSeats },
  'ai.video': { enabled: false, limit: 0 },
  'ai.generation': { enabled: false, limit: S.aiGenerations },
  'ai.replies.auto_send': { enabled: false, limit: null },
  'occasions.posters': { enabled: false, limit: S.occasionPosters },
  'ai.replies': { enabled: false, limit: S.aiReplyDrafts },
  'planner.plan_metered': { enabled: false, limit: null },
  'planner.regenerate_metered': { enabled: true, limit: null },
  'planner.weekly': { enabled: false, limit: null },
  'insights.customers': { enabled: false, limit: null },
  'billing.refund_request': { enabled: true, limit: null },
  'publish.verify': { enabled: true, limit: null },
  'x.post_credits': { enabled: false, limit: S.xPostCredits },
  'inbox.read': { enabled: true, limit: null },
  'inbox.reply': { enabled: false, limit: null },
  'inbox.assign': { enabled: false, limit: null },
  'inbox.sla': { enabled: false, limit: null },
  'analytics.retention_days': { enabled: true, limit: S.analyticsRetentionDays },
  'analytics.csv_export': { enabled: S.csvExport, limit: null },
  'storage.limit_bytes': { enabled: true, limit: S.storageBytes },
  'templates.personal': { enabled: false, limit: null },
  'templates.shared': { enabled: false, limit: null },
  'workflow.approval': { enabled: S.approvalWorkflow, limit: null },
  'workspaces.limit': { enabled: true, limit: S.maxWorkspaces },
  'advanced.filtering': { enabled: S.advancedFiltering, limit: null },
  'advanced.activity_log': { enabled: false, limit: S.activityLogDays },
  'advanced.client_viewer': { enabled: S.clientViewerAccess, limit: null },
  'advanced.api_access': { enabled: S.apiAccess, limit: null },
  'advanced.white_label': { enabled: S.whiteLabel, limit: null },
  // Channel availability: plan columns are all on; how many channels a plan
  // connects is platforms.connect. A pasted WhatsApp token stays off.
  'channel.telegram': { enabled: true, limit: null },
  'channel.linkedin': { enabled: true, limit: null },
  'channel.twitter': { enabled: true, limit: null },
  'channel.instagram': { enabled: true, limit: null },
  'channel.facebook': { enabled: true, limit: null },
  'channel.whatsapp': { enabled: true, limit: null },
  'channel.youtube': { enabled: true, limit: null },
  'channel.google_business': { enabled: true, limit: null },
  'channel.tiktok': { enabled: true, limit: null },
  'channel.pinterest': { enabled: true, limit: null },
  'channel.zetpin': { enabled: true, limit: null },
  'channel.whatsapp.manualToken': { enabled: false, limit: null },
};

/** Starter's value on a flag; a flag not listed is off on Starter. */
export function starterFlagValue(key: string): StarterFlagValue {
  return STARTER_PLAN_FLAGS[key] ?? { enabled: false, limit: null };
}
